import "@app/App.css";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { toastError } from "@utils/toasts";
import { TW_ObjectModelViewHeaderButton, TW_ObjectModelViewHeaderSmallerIcon } from "@utils/twClasses";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useDataStore, useModelStore } from "src/store";
import { exportView } from "@utils/data";

function ExportButton() {
  const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
  const selectedWorkspace = useModelStore((state) => state.selectedWorkspace);
  const selectedView = useModelStore((state) => state.selectedView);
  const period = useDataStore((state) => state.period);
  const { t } = useTranslation();
  const loadingData = useDataStore((state) => state.loadingData);
  const loadData = useDataStore((state) => state.loadData);
  const stopLoadingData = useDataStore((state) => state.stopLoadingData);
  const fields = selectedObjectInfos?.fields || null;

  const handleExport = async () => {
    if (!selectedWorkspace?._key || !selectedObjectInfos?.object._key) {
      console.error("Selected workspace or object key not found");
      return;
    }

    try {
      loadData();
      
      const response = await exportView(selectedView, period);

      if (!response) {
        throw new Error(`No response from exportView`);
      }

      const result = await response;
      
      if (isCompozzError(result)) {
        const cError = result as CompozzError;
        if (cError.i18n) {
          toastError(translate(t, cError.i18n, cError.params));
        } else {
          toastError(cError.message);
        }
        stopLoadingData();
        return;
      }

      // Décoder le base64 et télécharger le fichier
      const binaryString = atob(result); // Décoder le base64
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const csvData = new TextDecoder('utf-8').decode(bytes);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      
      // Créer un lien de téléchargement
      const link = document.createElement('a');
      link.href = url;

      let filename = 'export_';
      if (selectedObjectInfos.object.name) {
        filename += selectedObjectInfos.object.name;
      } else {
        filename += 'data';
      }
      if (selectedView?.name) {
        filename += '_' + selectedView?.name;
      }
      filename += '_' + new Date().toISOString().split('T')[0] + '.csv';

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      stopLoadingData();
    } catch (error) {
      console.error('Export error:', error);
      toastError(t('record.csv.export.error'));
      stopLoadingData();
    }
  };

  const isDisabled = (!fields || fields.length === 0) || loadingData || (selectedView?.groups?.length !== undefined && selectedView.groups.length > 0);
  
  return (
    <TooltipProvider>
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <button 
            className={TW_ObjectModelViewHeaderButton + (isDisabled ? ' opacity-50 cursor-not-allowed' : ' opacity-100 cursor-pointer')} 
            onClick={handleExport}
            disabled={isDisabled}
          >
            <Download className={TW_ObjectModelViewHeaderSmallerIcon} />
          </button>
        </TooltipTrigger>
        <TooltipContent>
          {(isDisabled ? t('object.header.button.export.tooltip.disabled') : t('object.header.button.export.tooltip'))}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default ExportButton;
